import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Stack, Typography } from '@mui/material'
import { useDimCatalog, useValueLabels } from '~/api/hooks/useCatalog'
import { cleanValueOptions, sortDimNames } from './optionListOrder'
import { useFilterDimsState } from '~/state/useFilterDimsState'
import { useGroupByState } from '~/state/useGroupByState'
import { useColumnsState } from '~/state/useColumnsState'
import { useSetMultipleSearchParams } from '~/state/useSetMultipleSearchParams'
import type { ActiveCampaign } from '~/state/useCampaignState'
import { ALL_CAMPAIGN_DIMS, CAMPAIGN_EXACT_DIM, mergeArmFilters } from '~/state/campaignPredicate'
import { GroupBySelect } from './GroupBySelect'
import { ActiveFilterChip } from './ActiveFilterChip'
import { AddFilterMenu } from './AddFilterMenu'
import { ColumnsSelect } from './ColumnsSelect'
import { useFilterApplicability } from '~/components/ReviewPage/useFilterApplicability'
import { pageGestalt, pageSupportsColumns } from '~/components/ReviewPage/usePageGestalt'
import { useDimValueLabelFor } from '~/components/ReviewPage/useDimValueLabelFor'
import { usePageControls, usePageCharts } from '~/api/hooks/useCharts'
import { useDriverArms } from './useDriverArms'
import { computeBreakerDims } from './breakerDims'
import { labelFor } from '~/components/ReviewPage/kpiLabel'
import { useRatioDimApplicability } from '~/api/hooks/useRatioDimApplicability'
import { ratioDimWarningFor } from './ratioDimWarning'
import {
  CONTROLS,
  filterDropsGroupsNote,
  filterNoDataNote,
  groupByNotApplicableNote,
  MESSAGES,
} from '~/components/Charts/chrome/chartChrome'
// [round-3 port] master retired CONTROLS.filters with the ControlBand redesign; release's FU-09a caption still needs it.
import { LEGACY_CONTROLS } from '~/components/Charts/chrome/legacyControlCopy'

interface Props {
  factTable: string
  kpi: string
  /** FU-m-2: the page id whose chart_registry carries the driver binding (drives the group-by ⚠). */
  pageId: string
  /** The page's seeded group-by default (CONTRIBUTION_VIEWS decomposition seed) — keeps this panel's
   *  useGroupByState in agreement with useReviewPageState's seeded call. */
  defaultGroupBy?: string
  /** C3 (spec §3.3 + D-C3-2): the reconciled active campaign, or null. Campaign selection writes
   *  identity-only URLs (Task 2) — without this, a carrying-page campaign selection would render
   *  as an invisible filter. Drives the synthetic exact/attribution chips below. */
  campaign?: ActiveCampaign | null
}

export function DimFilterPanel({
  factTable,
  kpi,
  pageId,
  defaultGroupBy = '',
  campaign = null,
}: Props) {
  const [filters, setFilters] = useFilterDimsState()
  const [groupBy, setGroupBy] = useGroupByState(defaultGroupBy)
  const [columns, setColumns] = useColumnsState(groupBy)
  const setMultipleSearchParams = useSetMultipleSearchParams()
  const [lastAddedDim, setLastAddedDim] = useState<string | null>(null)
  const dimCatalog = useDimCatalog()
  const valueLabels = useValueLabels(factTable)
  const { driverFacts, driverKpi } = useDriverArms(pageId, kpi)
  const controls = usePageControls(factTable)
  const pageCharts = usePageCharts(pageId, kpi)
  const supportsColumns = pageSupportsColumns(pageGestalt(pageCharts))

  // Row 23: per-dim applicability over the APPLIED set — URL filters merged with the
  // campaign's resolved filters, matching what the walks receive via baseParams.filter_dims
  // (FU-29; restores parity with FilterApplicabilityNote's call site). No campaign ⇒
  // mergeArmFilters returns `filters` by reference ⇒ byte-identical to the old input.
  const appliedFilters = campaign ? mergeArmFilters(filters, campaign.resolvedFilters) : filters
  const applicability = useFilterApplicability({ factTable, kpi, groupBy, filters: appliedFilters })
  const groupValueLabelFor = useDimValueLabelFor(factTable)
  const warningForDim = (dim: string): string | undefined => {
    if (applicability.status === 'empty' && applicability.inapplicableDims.includes(dim)) {
      return filterNoDataNote([dim])
    }
    if (applicability.status === 'partial' && applicability.byDim[dim]) {
      return filterDropsGroupsNote(
        dim,
        applicability.byDim[dim].map((g) => groupValueLabelFor(applicability.groupDim, g)),
        applicability.expectedCount,
      )
    }
    return undefined
  }

  // M1: clear ghost once the dim has graduated with at least one value picked.
  // The non-empty check keeps the ghost in place when the user clears all values
  // mid-edit (M5) — a transiently empty filters[dim] is the M5 hand-off signal.
  useEffect(() => {
    if (lastAddedDim && (filters[lastAddedDim]?.length ?? 0) > 0) {
      setLastAddedDim(null)
    }
  }, [filters, lastAddedDim])

  const dimsForFact = useMemo(
    () => (dimCatalog.data ?? []).filter((r) => r.fact_table === factTable),
    [dimCatalog.data, factTable],
  )

  // Page+kpi filtering of value labels per spec §5.6.I.
  // Page filter dropped pending analyst conventions: the seed binds page='Marketing Invests'
  // for every value-label even though those labels are reused across pages (spec §5.6 says
  // "revisit if too restrictive on first analyst feedback" — this is that moment).
  // For parent_kpi the seed uses the literal string 'any' as the universal sentinel; treat
  // it alongside null/undefined.
  const labelsForPageKpi = useMemo(() => {
    if (!valueLabels.data) return []
    return valueLabels.data.filter(
      (r) => r.parent_kpi == null || r.parent_kpi === 'any' || r.parent_kpi === kpi,
    )
  }, [valueLabels.data, kpi])

  const optionsByDim = useMemo(() => {
    const map: Record<string, { value: string; label: string }[]> = {}
    for (const dim of dimsForFact) {
      map[dim.dim_name] = cleanValueOptions(
        labelsForPageKpi
          .filter((r) => r.dim_name === dim.dim_name)
          .map((r) => ({ value: r.value, label: r.display_label })),
      )
    }
    return map
  }, [dimsForFact, labelsForPageKpi])

  // SP-1 Task 3: ratio dim applicability — called unconditionally (Rules of Hooks).
  // The view returns rows only for ratio KPIs (sales_roi / sales_cpo / sales_opc /
  // sales_mediashare), so an empty result [] is the non-ratio signal. While data is
  // undefined (still loading) we fall back to the breakerDims path to avoid a flash of
  // missing dims.
  const ratioAppl = useRatioDimApplicability(kpi)
  const isRatioKpi = ratioAppl.data !== undefined && ratioAppl.data.length > 0

  if (dimCatalog.isError || valueLabels.isError) {
    return (
      <Alert severity="error">
        {MESSAGES.dimCatalogLoadFailed}{' '}
        <button
          onClick={() => {
            dimCatalog.refetch()
            valueLabels.refetch()
          }}
        >
          {MESSAGES.retry}
        </button>
      </Alert>
    )
  }
  if (dimsForFact.length === 0 && !dimCatalog.isLoading) {
    return <Alert severity="warning">{MESSAGES.noDimCatalogEntries(factTable)}</Alert>
  }

  const catalogDimNames = dimsForFact.map((d) => d.dim_name)

  // For a ratio KPI, offered dims = union of the catalog dims AND the applicability rows
  // where in_num || in_denom (so CpO gains the gauge dims the numerator fact doesn't carry).
  // For a non-ratio KPI, offered dims = catalog dims only (unchanged).
  const availableDims = sortDimNames(
    isRatioKpi
      ? Array.from(
          new Set([
            ...catalogDimNames,
            ...(ratioAppl.data ?? []).filter((r) => r.in_num || r.in_denom).map((r) => r.dim_name),
          ]),
        )
      : catalogDimNames,
  )

  // FU-m-2: pre-selection ⚠ for a group-by dim a sibling driver fact can't carry (mirrors the
  // post-selection typed DimNotApplicableError). Driver label matches the tile rows' labelFor.
  // SP-1 Task 3: for ratio KPIs, replace the generic breakerDims warning with the precise
  // per-side ⚠ (numerator-only / denominator-only). The ⚠ is advisory only — never disables.
  const breakers = computeBreakerDims(availableDims, driverFacts, dimCatalog.data ?? [])
  const driverLabel = driverKpi ? labelFor(controls, driverKpi) : undefined
  const groupByWarningFor: (dim: string) => string | undefined = isRatioKpi
    ? ratioDimWarningFor(ratioAppl.data ?? [])
    : (dim) => (breakers.has(dim) ? groupByNotApplicableNote([dim], driverLabel) : undefined)
  // Active dims = those persisted in URL state PLUS the just-added "ghost" dim.
  // useFilterDimsState is URL-backed; encodeDimFilters drops empty-array entries,
  // so a dim with no values picked yet cannot live in `filters`. We render it as
  // a chip via lastAddedDim and wait for the first value pick before calling
  // setFilters — at which point the dim enters `filters` legitimately.
  const activeDims =
    lastAddedDim && !(lastAddedDim in filters)
      ? [...Object.keys(filters), lastAddedDim]
      : Object.keys(filters)

  const handleAddDim = (dim: string) => {
    // Don't write to filters yet (empty arrays don't survive URL round-trip).
    // The ghost chip renders via the activeDims union above; setFilters fires
    // when the user picks the first value via handleChipChange.
    setLastAddedDim(dim)
  }
  const handleChipChange = (dim: string, next: string[]) => {
    setFilters({ ...filters, [dim]: next })
    // M5: empty arrays don't survive URL round-trip; without re-arming the ghost
    // here the chip would unmount mid-edit. Re-using lastAddedDim mirrors the
    // just-added case so the popover stays open until the user picks again.
    if (next.length === 0) setLastAddedDim(dim)
  }
  const handleChipRemove = (dim: string) => {
    if (dim in filters) {
      const { [dim]: _removed, ...rest } = filters
      setFilters(rest)
    }
    if (lastAddedDim === dim) setLastAddedDim(null)
  }

  // Campaigns C3 (spec §3.3 + D-C3-2): synthetic chips render the campaign's fact-aware
  // resolvedFilters — {Campaign:[id]} on a carrying fact (ONE synthetic chip, contract point 1),
  // or the per-dim attribution predicate elsewhere (contract point 2). Selection writes
  // identity-only URLs (Task 2), so without these the campaign would be an invisible filter.
  // Contract point 4: skip any resolved dim ALREADY materialized as an explicit URL filter — it
  // already renders as an ordinary chip via activeDims above; no double-render.
  const syntheticEntries = campaign
    ? Object.entries(campaign.resolvedFilters).filter(([dim]) => !(dim in filters))
    : []

  // The exact `Campaign` dim's value label is the campaign's own friendly label (spec: "the
  // exact chip's VALUE label is the campaign's label from campaign.item.label") — v_value_labels
  // has no entry for a synthetic identity value. Attribution dims are real catalog dims, so they
  // go through the SAME labeling path as ordinary URL chips (optionsByDim) — no fork.
  const syntheticOptionsFor = (dim: string): { value: string; label: string }[] =>
    dim === CAMPAIGN_EXACT_DIM && campaign
      ? [{ value: campaign.id, label: campaign.item.label }]
      : (optionsByDim[dim] ?? [])

  // Editing follows materialize-then-diverge: the OTHER not-yet-materialized resolved dims are
  // written unchanged alongside the edited one (so nothing is silently lost), and the `campaign`
  // param is left untouched — the reconciliation effect (useReviewPageState) detects the
  // resulting divergence from the campaign's predicate and collapses `campaign` on its own.
  const handleSyntheticChange = (dim: string, next: string[]) => {
    // FU-09(b): unchecking every value is the chip's clear gesture. The materialize-then-diverge
    // write below cannot express it — an empty dim.* entry doesn't survive the URL round-trip
    // and the untouched `campaign` param re-materializes the dim unchanged (a silent no-op) —
    // so delegate to the delete path (campaign cleared, OTHER resolved dims preserved).
    if (next.length === 0) {
      handleSyntheticRemove(dim)
      return
    }
    const materialized = { ...filters }
    for (const [otherDim, values] of syntheticEntries) {
      if (otherDim !== dim && !(otherDim in materialized)) materialized[otherDim] = values
    }
    materialized[dim] = next
    setFilters(materialized)
  }

  // Delete reproduces C1's "campaign cleared, other chip untouched" outcome-parity: the OTHER
  // resolved dims are written as explicit dim.<name> params (so they survive the campaign's
  // collapse) and `campaign` is cleared — one atomic write via useSetMultipleSearchParams (the
  // useFilterDimsState setter would clobber every existing dim.* param, not just the deleted
  // one). In the carrying case (single {Campaign:[id]} entry) this degenerates to exactly
  // { campaign: null } — contract point 1's "dates stay" gesture.
  const handleSyntheticRemove = (dim: string) => {
    const params: Record<string, string | null> = { campaign: null }
    for (const [otherDim, values] of syntheticEntries) {
      if (otherDim === dim) continue
      params[`dim.${otherDim}`] = values.join(',')
    }
    setMultipleSearchParams(params)
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {LEGACY_CONTROLS.filters}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {syntheticEntries.map(([dim, values]) => (
            <ActiveFilterChip
              key={`campaign-${dim}`}
              dimName={dim}
              options={syntheticOptionsFor(dim)}
              selected={values}
              // FU-09a wired this prop for parity with the ordinary site below; FU-29's applied-set
              // widening above (appliedFilters) is what lights it up — warningForDim now sees this
              // dim among the merged filters it's evaluated against, so it can mark a synthetic chip
              // exactly like an ordinary URL-filter chip.
              // [vodafone-few port] release keeps the FU-09a Filters-caption chrome (C3b-port delta);
              // only FU-29's comment content is adopted — the warning wiring is byte-identical.
              warning={warningForDim(dim)}
              onChange={(next) => handleSyntheticChange(dim, next)}
              onRemove={() => handleSyntheticRemove(dim)}
            />
          ))}
          {activeDims.map((dim) => (
            <ActiveFilterChip
              key={dim}
              dimName={dim}
              options={optionsByDim[dim] ?? []}
              selected={filters[dim] ?? []}
              defaultOpen={dim === lastAddedDim}
              warning={warningForDim(dim)}
              // FU-32: membership is the whole predicate — a stably coexisting owned-dim chip is
              // redundant by construction (divergence collapses the campaign upstream).
              campaignCovered={
                campaign && ALL_CAMPAIGN_DIMS.includes(dim)
                  ? CONTROLS.campaignCoveredChip
                  : undefined
              }
              onChange={(next) => handleChipChange(dim, next)}
              onRemove={() => handleChipRemove(dim)}
            />
          ))}
          <AddFilterMenu
            availableDims={availableDims}
            activeDims={activeDims}
            onAddDim={handleAddDim}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {CONTROLS.groupBy}
        </Typography>
        <GroupBySelect
          options={availableDims}
          value={groupBy}
          onChange={setGroupBy}
          warningFor={groupByWarningFor}
        />
      </Box>

      {supportsColumns ? (
        <Box>
          <ColumnsSelect groupBy={groupBy} value={columns} onChange={setColumns} />
        </Box>
      ) : null}
    </Stack>
  )
}
